import React, { useContext, useState } from "react";

import { getDatabase, ref, remove } from "firebase/database";
import { TextField, Button, CircularProgress, Paper, Alert, Dialog, DialogActions, DialogTitle, DialogContent, Snackbar } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import Form from '../../components/Form/Form';

import GamesContext from '../../store/games-context';
import RunsContext from '../../store/runs-context';
import Run from '../../types/run';
import { formatDate } from '../../utils/utils';
import { handleSubmit } from '../../utils/form.utils';
import SucceedIcon from '../../components/Icons/Succeed';
import FailedIcon from '../../components/Icons/Failed';
import styles from './editrun.module.scss';

const Editor = () => {
  const gamesContext = useContext(GamesContext);
  const runsContext = useContext(RunsContext);
  // const userContext = useContext(UserContext);
  const games = gamesContext.games;
  const [date, updateDate] = useState<string>(formatDate(new Date()));
  const [removalConfirmation, updateRemovalConfirmation] = useState<boolean>(false);
  const [removalError, updateRemovalError] = useState<boolean>(false);
  const [editConfirmation, updateEditConfirmation] = useState<boolean>(false);
  const [editError, updateEditError] = useState<boolean>(false);
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState<boolean>(false);
  const [toRemoveRun, setToRemoveRun] = useState<Run | undefined>();
  const [run, onRunSelected] = useState<Run | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);

  const openDeleteConfirmation = async (run: Run) => {
    setToRemoveRun(run);
    setDeleteConfirmationModalOpen(true);
  }

  const handleConfirmDelete = () => {
    if (toRemoveRun){
      deleteRun(toRemoveRun);
      setDeleteConfirmationModalOpen(false);
    }
  };

  const deleteRun = async (run: Run) => {
    const globalWindow = (window as any);
    const db = getDatabase(globalWindow.firebaseApp);
    if(!!run._id) {
      remove(ref(db, `/run/${run._id}`))
        .then(() => {
          updateRemovalConfirmation(true);
          runsContext.removeRun?.(run);
        })
        .catch(e => {
          updateRemovalError(true);
        });
    }
  };
  const filteredRuns = runsContext.runs.filter((run) => run.date === formatDate(date));
  
  if (games && games.length > 0) {
    if(!editMode) {
      return (
        <div className={styles.container}>
          <h2>Edit Run</h2>
          <h3>Select Run</h3>
          <DatePicker
            label="Select Date"
            className={'test'}
            value={date}
            onChange={e => e && updateDate(e)}
            renderInput={(params) => <TextField {...params} />}
          />
          <Paper className={styles.runsContainer}>
            {filteredRuns.map(run => {
              const runIcon = !run.includesHit ? <span className={styles.successfulRun}><SucceedIcon /></span> : <span className={styles.failedRun}><FailedIcon /></span>;
              const runIconComponent = run.partial && !!run.endSplit ?  <span className={styles.partialRun} /> : runIcon;
              return (
                <div className={styles.listItem}>
                  <span className={styles.runText}>
                  {runIconComponent} <span className={styles.innerRunText}>({run.order}) {games[run.selectedGame].name}</span>
                  </span>
                  <span className={styles.buttonContainer}>
                    <Button size="large" variant="contained" color="secondary" onClick={() => {
                      setEditMode(true);
                      onRunSelected(run);
                    }}>
                      Update Run
                    </Button>
                  </span>
                  <span>
                    <Button size="small" variant="outlined" color="warning" startIcon={<DeleteIcon />} onClick={() => {
                      openDeleteConfirmation(run);
                    }}>
                      Delete Run
                    </Button>
                  </span>
                </div>
              )
            })}
          </Paper>
          <Dialog
            open={deleteConfirmationModalOpen}
            onClose={() => setDeleteConfirmationModalOpen(false)}
            className={styles.modal}
            closeAfterTransition
            maxWidth={'xl'}
            BackdropProps={{
              timeout: 500,
            }}>
              <DialogTitle>
                Do you really want to remove the Run?
              </DialogTitle>
              <DialogContent>
                It will remove permanently the indicated Run
              </DialogContent>
              <DialogActions>
                <Button size="small" variant="outlined" color="secondary" onClick={() => setDeleteConfirmationModalOpen(false)}>Cancel</Button>
                <Button variant="contained" color="warning" onClick={handleConfirmDelete}
                 autoFocus>Remove </Button>
              </DialogActions>
          </Dialog>
          <Snackbar open={editConfirmation} autoHideDuration={6000} onClose={() => updateEditConfirmation(false)}>
            <Alert onClose={() => updateEditConfirmation(false)} severity="success" sx={{ width: '100%' }}>
              Run Edited succesfully!
            </Alert>
          </Snackbar>
          <Snackbar open={editError} autoHideDuration={6000} onClose={() => updateEditError(false)}>
            <Alert onClose={() => updateEditError(false)} severity="error" sx={{ width: '100%' }}>
            Unable to edit, please try again later
            </Alert>
          </Snackbar>
          <Snackbar open={removalConfirmation} autoHideDuration={6000} onClose={() => updateRemovalConfirmation(false)}>
            <Alert onClose={() => updateRemovalConfirmation(false)} severity="success" sx={{ width: '100%' }}>
            Run Removed succesfully!
            </Alert>
          </Snackbar>
          <Snackbar open={removalError} autoHideDuration={6000} onClose={() => updateRemovalError(false)}>
            <Alert onClose={() => updateRemovalError(false)} severity="error" sx={{ width: '100%' }}>
              Unable to remove, please try again later
            </Alert>
          </Snackbar>
        </div>
      );
    } else {
      return (
        <div className={styles.container}>
          <h2>Edit Run</h2>
          <div>
            <Form
              {...run as Run}
              handleSubmit={(args: any) => {
                handleSubmit(args);
                setEditMode(false);
              }}
              onCancel={() => setEditMode(false)}
              onSuccess={() => updateEditConfirmation(true)}
              onError={() => updateEditError(true)}
            />
          </div>
        </div>
      )
    }
  } else {
    return (
      <div className={styles.loaderContainer}>
        <CircularProgress className={styles.loader} />
      </div>
    );
  }
}

export default Editor;